import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Code, Image, Zap,
  Clock, CheckCircle2, XCircle, ArrowRight,
  Crown, Activity, BarChart3, Gauge,
} from 'lucide-react';
import { api } from '../api.js';
import { useAuthStore } from '../store/auth.js';
import { useT } from '../i18n/useT.js';

function pct(used, limit) {
  if (!limit || limit < 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function fmtNumber(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function timeAgo(dateStr, t) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('dashboard.timeJustNow', 'just now');
  if (mins < 60) return t('dashboard.timeMinutesAgo', '{{count}}m ago', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('dashboard.timeHoursAgo', '{{count}}h ago', { count: hrs });
  return t('dashboard.timeDaysAgo', '{{count}}d ago', { count: Math.floor(hrs / 24) });
}

function labelFromFeatureKey(key, t) {
  const map = {
    studio: t('dashboard.feature.studio', 'Studio'),
    apiAccess: t('dashboard.feature.apiAccess', 'API Access'),
    priorityQueue: t('dashboard.feature.priorityQueue', 'Priority Queue'),
    customBranding: t('dashboard.feature.customBranding', 'Custom Branding'),
    aiGeneration: t('dashboard.feature.aiGeneration', 'AI Generation'),
    visualBuilder: t('dashboard.feature.visualBuilder', 'Visual Builder'),
    scheduledJobs: t('dashboard.feature.scheduledJobs', 'Scheduled Jobs'),
    templateVersioning: t('dashboard.feature.templateVersioning', 'Template Versioning'),
  };
  if (map[key]) return map[key];
  return String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
}

function AnimCounter({ target, duration = 1200 }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    let start = null;
    const to = Number(target || 0);
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(progress * to));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);

  return <span>{fmtNumber(val)}</span>;
}

function ArcGauge({ value, max, label, color }) {
  const isUnlimited = max === -1 || max === undefined || max === null;
  const percentage = isUnlimited ? 100 : pct(value, max);
  const r = 42;
  const cx = 56;
  const cy = 56;
  const startAngle = -215;
  const totalAngle = 250;
  const angle = (percentage / 100) * totalAngle + startAngle;
  const toRad = (deg) => (deg * Math.PI) / 180;

  const arcPath = (start, end, rx) => {
    const s = { x: cx + rx * Math.cos(toRad(start)), y: cy + rx * Math.sin(toRad(start)) };
    const e = { x: cx + rx * Math.cos(toRad(end)), y: cy + rx * Math.sin(toRad(end)) };
    const large = end - start > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${rx} ${rx} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const gaugeColor = percentage > 90 ? '#f87171' : percentage > 70 ? '#fbbf24' : color;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width="112" height="100" viewBox="0 0 112 100">
        <path d={arcPath(startAngle, startAngle + totalAngle, r)} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" strokeLinecap="round" />
        <path d={arcPath(startAngle, angle, r)} fill="none" stroke={gaugeColor} strokeWidth="7" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 6px ${gaugeColor}88)` }} />
        <text x={cx} y={cy - 2} textAnchor="middle" fill="#f8fafc" fontSize="14" fontWeight="700" fontFamily="'DM Mono', monospace">
          {isUnlimited ? '∞' : `${percentage}%`}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="8" fontFamily="'DM Sans', sans-serif">
          {fmtNumber(value)} / {isUnlimited ? '∞' : fmtNumber(max)}
        </text>
      </svg>
      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
    </div>
  );
}

export default function DashboardPage() {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 50);
  }, []);

  useEffect(() => {
    let active = true;

    api.getDashboardStats()
      .then((res) => {
        if (!active) return;
        setStats(res || null);
      })
      .catch(() => {
        if (!active) return;
        setStats(null);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const safe = useMemo(() => ({
    plan: stats?.plan || { name: 'Free', monthlyPrice: 0 },
    limits: stats?.limits || {},
    usage: stats?.usage || {},
    counts: stats?.counts || { templates: 0, scripts: 0, assets: 0 },
    jobStats: stats?.jobStats || { succeeded: 0, failed: 0, running: 0, queued: 0 },
    recentJobs: Array.isArray(stats?.recentJobs) ? stats.recentJobs : [],
    features: stats?.features || {},
    subscription: stats?.subscription || { status: 'inactive' },
  }), [stats]);

  const { plan, limits, usage, counts, jobStats, recentJobs, features, subscription } = safe;

  const totalJobs = Object.values(jobStats).reduce((acc, value) => acc + Number(value || 0), 0);
  const successRate = totalJobs > 0 ? Math.round(((Number(jobStats.succeeded || 0)) / totalJobs) * 100) : 100;

  const r = 54;
  const circ = 2 * Math.PI * r;
  const srColor = successRate > 90 ? '#22d3a5' : successRate > 70 ? '#fbbf24' : '#f87171';

  const userName = user?.name || user?.email?.split('@')[0] || t('dashboard.userFallback', 'User');

  const STATUS = {
    succeeded: { color: '#22d3a5', label: t('dashboard.succeeded', 'Succeeded'), Icon: CheckCircle2 },
    failed: { color: '#f87171', label: t('dashboard.failed', 'Failed'), Icon: XCircle },
    running: { color: '#60a5fa', label: t('dashboard.running', 'Running'), Icon: Activity },
    queued: { color: '#fbbf24', label: t('dashboard.queued', 'Queued'), Icon: Clock },
  };

  const quickStats = [
    { label: t('dashboard.templates', 'Templates'), value: counts.templates, icon: FileText, color: '#818cf8', bg: 'rgba(129,140,248,0.12)', route: '/templates' },
    { label: t('dashboard.scripts', 'Scripts'), value: counts.scripts, icon: Code, color: '#34d399', bg: 'rgba(52,211,153,0.12)', route: '/scripts' },
    { label: t('dashboard.assets', 'Assets'), value: counts.assets, icon: Image, color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', route: '/assets' },
    { label: t('dashboard.totalJobs', 'Total Jobs'), value: totalJobs, icon: BarChart3, color: '#f87171', bg: 'rgba(248,113,113,0.12)', route: '/jobs' },
  ];

  const planRows = [
    { key: t('dashboard.apiRate', 'API Rate'), val: limits.apiCallsPerMinute === -1 ? '∞' : `${fmtNumber(limits.apiCallsPerMinute)}/min` },
    { key: t('dashboard.concurrency', 'Concurrency'), val: limits.concurrentRenders === -1 ? '∞' : fmtNumber(limits.concurrentRenders) },
  ];

  const featureRows = Object.entries(features).filter(([, value]) => typeof value === 'boolean');

  return (
    <div className="page-content">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #080b14; }
        .db-root {
          min-height: 100vh;
          background: #080b14;
          font-family: 'DM Sans', sans-serif;
          color: #f8fafc;
          padding: 32px 24px;
          max-width: 1280px;
          margin: 0 auto;
        }
        .db-root::before {
          content: '';
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          opacity: 0.4;
        }
        .orb {
          position: fixed; border-radius: 50%; pointer-events: none; z-index: 0;
          filter: blur(80px); opacity: 0.18;
          animation: orbFloat 8s ease-in-out infinite alternate;
        }
        .orb-1 { width: 500px; height: 500px; background: #4f46e5; top: -150px; left: -100px; animation-delay: 0s; }
        .orb-2 { width: 400px; height: 400px; background: #0ea5e9; top: 40%; right: -120px; animation-delay: -3s; }
        .orb-3 { width: 300px; height: 300px; background: #7c3aed; bottom: -80px; left: 30%; animation-delay: -5s; }
        @keyframes orbFloat { from { transform: translate(0,0) scale(1); } to { transform: translate(20px, 30px) scale(1.08); } }

        .db-content { position: relative; z-index: 1; }
        .fade-in { opacity: 0; transform: translateY(18px); transition: opacity 0.55s ease, transform 0.55s ease; }
        .fade-in.visible { opacity: 1; transform: translateY(0); }

        .db-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 40px; flex-wrap: wrap; gap: 16px;
        }
        .db-header__greeting {
          font-family: 'Fraunces', serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 300;
          line-height: 1.15;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #f8fafc 30%, #94a3b8);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        .db-header__sub { color: rgba(255,255,255,0.4); font-size: 14px; margin-top: 6px; letter-spacing: 0.02em; }
        .db-plan-pill {
          display: flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, rgba(79,70,229,0.25), rgba(124,58,237,0.25));
          border: 1px solid rgba(129,140,248,0.3);
          padding: 10px 18px; border-radius: 100px;
          font-size: 13px; font-weight: 600;
          backdrop-filter: blur(12px);
          color: #c4b5fd;
        }
        .db-plan-pill__price { font-family: 'DM Mono', monospace; color: rgba(196,181,253,0.6); font-size: 11px; }

        .db-quick { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 32px; }
        @media (max-width: 768px) { .db-quick { grid-template-columns: repeat(2, 1fr); } }
        .db-stat {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          padding: 22px 20px;
          cursor: pointer;
          transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
          display: flex; align-items: center; gap: 14px;
          position: relative; overflow: hidden;
          backdrop-filter: blur(12px);
        }
        .db-stat::after {
          content: ''; position: absolute; inset: 0; border-radius: 20px;
          background: linear-gradient(135deg, var(--c) 0%, transparent 60%);
          opacity: 0; transition: opacity 0.2s ease;
        }
        .db-stat:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.14); }
        .db-stat:hover::after { opacity: 0.06; }
        .db-stat__icon {
          width: 46px; height: 46px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .db-stat__val { font-family: 'DM Mono', monospace; font-size: 26px; font-weight: 500; line-height: 1; }
        .db-stat__label { font-size: 12px; color: rgba(255,255,255,0.45); margin-top: 3px; letter-spacing: 0.04em; }
        .db-stat__arrow { margin-left: auto; color: rgba(255,255,255,0.2); transition: color 0.2s, transform 0.2s; }
        .db-stat:hover .db-stat__arrow { color: rgba(255,255,255,0.6); transform: translateX(3px); }

        .db-section-title {
          display: flex; align-items: center; gap: 8px;
          font-size: 12px; text-transform: uppercase; letter-spacing: 0.12em;
          color: rgba(255,255,255,0.35); margin-bottom: 18px;
          font-weight: 600;
        }
        .db-section-title::after {
          content: ''; flex: 1; height: 1px;
          background: linear-gradient(90deg, rgba(255,255,255,0.08), transparent);
        }

        .db-gauges {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 24px;
          padding: 32px;
          display: flex; justify-content: space-around; flex-wrap: wrap; gap: 24px;
          margin-bottom: 32px;
          backdrop-filter: blur(12px);
        }

        .db-bottom { display: grid; grid-template-columns: 1fr 1fr 1.4fr; gap: 18px; }
        @media (max-width: 900px) { .db-bottom { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 620px) { .db-bottom { grid-template-columns: 1fr; } }

        .db-card {
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 24px;
          padding: 28px 24px;
          backdrop-filter: blur(12px);
        }
        .db-card__title {
          font-size: 13px; font-weight: 600;
          color: rgba(255,255,255,0.55); letter-spacing: 0.05em;
          text-transform: uppercase; margin-bottom: 24px;
          display: flex; align-items: center; gap: 7px;
        }

        .db-health-center {
          display: flex; flex-direction: column; align-items: center; gap: 20px;
        }
        .db-health-ring-wrap { position: relative; display: flex; align-items: center; justify-content: center; }
        .db-health-ring-inner { position: absolute; text-align: center; }
        .db-health-pct {
          font-family: 'DM Mono', monospace; font-size: 28px; font-weight: 500;
          display: block; line-height: 1;
        }
        .db-health-sub { font-size: 10px; color: rgba(255,255,255,0.35); letter-spacing: 0.1em; text-transform: uppercase; }
        .db-breakdown { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; width: 100%; }
        .db-breakdown-item {
          display: flex; align-items: center; gap: 7px;
          font-size: 12px; color: rgba(255,255,255,0.5);
          background: rgba(255,255,255,0.04); border-radius: 10px; padding: 9px 12px;
        }
        .db-breakdown-item span:last-child { font-family: 'DM Mono', monospace; margin-left: auto; font-size: 13px; color: #f8fafc; }

        .db-feature-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 11px 0; border-bottom: 1px solid rgba(255,255,255,0.05);
          font-size: 13px;
        }
        .db-feature-row:last-of-type { border-bottom: none; }
        .db-feature-key { color: rgba(255,255,255,0.45); }
        .db-feature-val { font-family: 'DM Mono', monospace; color: #f8fafc; font-size: 12px; }
        .db-badge {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
          padding: 3px 9px; border-radius: 100px;
        }
        .db-badge--on  { background: rgba(34,211,165,0.15); color: #22d3a5; border: 1px solid rgba(34,211,165,0.25); }
        .db-badge--off { background: rgba(248,113,113,0.1); color: #f87171; border: 1px solid rgba(248,113,113,0.2); }

        .db-job {
          display: flex; align-items: center; gap: 12px;
          padding: 13px 16px;
          border-radius: 14px;
          margin-bottom: 8px;
          cursor: pointer;
          transition: background 0.18s;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .db-job:hover { background: rgba(255,255,255,0.04); }
        .db-job__dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .db-job__name { font-size: 13px; font-weight: 500; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .db-job__type {
          font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em;
          color: rgba(255,255,255,0.3); font-family: 'DM Mono', monospace;
          background: rgba(255,255,255,0.05); padding: 2px 7px; border-radius: 6px;
        }
        .db-job__time { font-size: 11px; color: rgba(255,255,255,0.3); font-family: 'DM Mono', monospace; white-space: nowrap; }
        .db-job__status {
          font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
          padding: 2px 8px; border-radius: 8px;
        }

        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

        .db-sub-status {
          display: flex; align-items: center; gap: 6px; margin-top: 16px;
          padding: 10px 14px; border-radius: 12px;
          background: rgba(34,211,165,0.08); border: 1px solid rgba(34,211,165,0.18);
          font-size: 12px; color: #22d3a5; font-weight: 600;
        }

        .db-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          color: rgba(255,255,255,0.6);
          font-size: 14px;
        }

        .db-error {
          text-align: center;
          color: rgba(248,113,113,0.9);
          border: 1px solid rgba(248,113,113,0.25);
          background: rgba(248,113,113,0.08);
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 20px;
        }
      `}</style>

      <div className="db-root">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />

        <div className="db-content">
          {loading && <div className="db-loading">{t('dashboard.loading', 'Loading dashboard...')}</div>}

          {!loading && !stats && <div className="db-error">{t('dashboard.loadFailed', 'Could not load dashboard data.')}</div>}

          {!loading && stats && (
            <>
              <div className={`db-header fade-in ${mounted ? 'visible' : ''}`} style={{ transitionDelay: '0ms' }}>
                <div>
                  <h1 className="db-header__greeting">{t('dashboard.welcome', 'Welcome back')},<br />{userName} 👋</h1>
                  <p className="db-header__sub">{t('dashboard.subtitle', 'Here\'s your report engine overview')}</p>
                </div>
                <div className="db-plan-pill">
                  <Crown size={15} />
                  <span>{plan.name || 'Free'}</span>
                  <span className="db-plan-pill__price">${Number(plan.monthlyPrice || 0)}/{t('dashboard.month', 'mo')}</span>
                </div>
              </div>

              <div className={`db-quick fade-in ${mounted ? 'visible' : ''}`} style={{ transitionDelay: '80ms' }}>
                {quickStats.map((s) => (
                  <div key={s.label} className="db-stat" style={{ '--c': s.color }} onClick={() => navigate(s.route)} role="button" tabIndex={0}>
                    <div className="db-stat__icon" style={{ background: s.bg }}>
                      <s.icon size={20} style={{ color: s.color }} />
                    </div>
                    <div>
                      <div className="db-stat__val" style={{ color: s.color }}>
                        <AnimCounter target={s.value} />
                      </div>
                      <div className="db-stat__label">{s.label}</div>
                    </div>
                    <ArrowRight size={14} className="db-stat__arrow" />
                  </div>
                ))}
              </div>

              <div className={`fade-in ${mounted ? 'visible' : ''}`} style={{ transitionDelay: '160ms' }}>
                <p className="db-section-title"><Gauge size={14} /> {t('dashboard.usageTitle', 'Plan Usage This Month')}</p>
                <div className="db-gauges">
                  <ArcGauge value={usage.rendersMonth || 0} max={limits.rendersMonth} label={t('dashboard.renders', 'Renders')} color="#818cf8" />
                  <ArcGauge value={counts.templates || 0} max={limits.templatesActive} label={t('dashboard.templatesActive', 'Active Templates')} color="#34d399" />
                  <ArcGauge value={usage.aiGenerationsMonth || 0} max={limits.aiGenerationsMonth} label={t('dashboard.aiGenerations', 'AI Generations')} color="#60a5fa" />
                  <ArcGauge value={usage.storageGB || 0} max={limits.storageGB} label={t('dashboard.storage', 'Storage (GB)')} color="#f59e0b" />
                </div>
              </div>

              <div className={`db-bottom fade-in ${mounted ? 'visible' : ''}`} style={{ transitionDelay: '240ms' }}>
                <div className="db-card">
                  <p className="db-card__title"><Crown size={14} /> {t('dashboard.planDetails', 'Plan Details')}</p>
                  {planRows.map((row) => (
                    <div key={row.key} className="db-feature-row">
                      <span className="db-feature-key">{row.key}</span>
                      <span className="db-feature-val">{row.val}</span>
                    </div>
                  ))}

                  {featureRows.map(([key, on]) => (
                    <div key={key} className="db-feature-row">
                      <span className="db-feature-key">{labelFromFeatureKey(key, t)}</span>
                      <span className={`db-badge ${on ? 'db-badge--on' : 'db-badge--off'}`}>{on ? t('dashboard.enabled', 'Enabled') : t('dashboard.disabled', 'Disabled')}</span>
                    </div>
                  ))}

                  <div className="db-sub-status">
                    <CheckCircle2 size={13} />
                    {t('dashboard.subscriptionStatus', 'Status')} {subscription.status === 'active' ? t('dashboard.active', 'active') : String(subscription.status || t('dashboard.inactive', 'inactive'))}
                  </div>
                </div>

                <div className="db-card">
                  <p className="db-card__title"><Activity size={14} /> {t('dashboard.jobHealth', 'Job Health')}</p>
                  <div className="db-health-center">
                    <div className="db-health-ring-wrap">
                      <svg width="136" height="136" viewBox="0 0 136 136">
                        <circle cx="68" cy="68" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                        <circle
                          cx="68"
                          cy="68"
                          r={r}
                          fill="none"
                          stroke={srColor}
                          strokeWidth="10"
                          strokeDasharray={circ}
                          strokeDashoffset={circ * (1 - successRate / 100)}
                          strokeLinecap="round"
                          transform="rotate(-90 68 68)"
                          style={{ filter: `drop-shadow(0 0 8px ${srColor}88)`, transition: 'stroke-dashoffset 1.2s ease' }}
                        />
                      </svg>
                      <div className="db-health-ring-inner">
                        <span className="db-health-pct" style={{ color: srColor }}>{successRate}%</span>
                        <span className="db-health-sub">{t('dashboard.successRate', 'Success')}</span>
                      </div>
                    </div>

                    <div className="db-breakdown">
                      {[
                        { key: t('dashboard.succeeded', 'Succeeded'), val: jobStats.succeeded || 0, color: '#22d3a5' },
                        { key: t('dashboard.failed', 'Failed'), val: jobStats.failed || 0, color: '#f87171' },
                        { key: t('dashboard.running', 'Running'), val: jobStats.running || 0, color: '#60a5fa' },
                        { key: t('dashboard.queued', 'Queued'), val: jobStats.queued || 0, color: '#fbbf24' },
                      ].map((item) => (
                        <div key={item.key} className="db-breakdown-item">
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, flexShrink: 0, boxShadow: `0 0 6px ${item.color}` }} />
                          <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>{item.key}</span>
                          <span>{item.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="db-card">
                  <p className="db-card__title"><Clock size={14} /> {t('dashboard.recentJobs', 'Recent Jobs')}</p>
                  {recentJobs.length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: '32px 0' }}>
                      <Clock size={28} style={{ marginBottom: 8, opacity: 0.4 }} /><br />{t('dashboard.noJobs', 'No jobs yet. Render a template to see activity here.')}
                    </div>
                  ) : recentJobs.map((job) => {
                    const s = STATUS[job.status] || STATUS.queued;
                    return (
                      <div key={job.id} className="db-job" onClick={() => navigate('/jobs')} role="button" tabIndex={0}>
                        <div
                          className="db-job__dot"
                          style={{
                            background: s.color,
                            boxShadow: `0 0 6px ${s.color}`,
                            animation: job.status === 'running' ? 'pulse 1.5s ease-in-out infinite' : 'none',
                          }}
                        />
                        <span className="db-job__name">{job.templateName || String(job.id || '').slice(0, 12)}</span>
                        <span className="db-job__type">{job.jobType || t('dashboard.job', 'job')}</span>
                        <span className="db-job__time">{timeAgo(job.createdAt, t)}</span>
                        <span className="db-job__status" style={{ color: s.color, background: `${s.color}18`, border: `1px solid ${s.color}30` }}>
                          {s.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
